<template>
<div class='ctc-view'>
  <div class='ctc-view__content'>
    <div class='ctc-view__content__properties'>
      <generic-properties
        :data='selectionProperties'
        @property-changed='handlePropertyChange'
      />
    </div>
    <div class='ctc-view__content__canvas-wrapper'>
      <div class='ctc-view__content__canvas-wrapper__controls'>
        <threat-canvas-controls
          :is-grid-on="isGridOn"
          :is-node-selected="!!selectedNodeId"
          :is-selected="!!selectedNodeId || !!selectedLinksIds"
          @threat-canvas-control-clicked="handleCanvasControl"
        />
      </div>
      <div class='ctc-view__content__canvas-wrapper__canvas' :style="canvasStyle" @contextmenu.prevent="openContextMenu($event)">
      <collapsable-linked-multi-tidy-tree
              :ref="`tree-${this._uid}`"
              :trees="filteredTrees"
              :customLinks="customLinks"
              :newNodePosition="newNodePosition"
              @node-selected="onNodeSelected"
              @link-selected="onLinkSelected"
              @canvas-selected="onCanvasSelected"
        />
      </div>
    </div>
  </div>
  <add-node-popup
    :visible-popup='showAddNodeDialog'
    title='ctc.add.node.popup.title'
    yesCaption="confirm.ok"
    noCaption="confirm.cancel"
    @cancel-add-node-clicked='showAddNodeDialog =!showAddNodeDialog'
    @add-node-clicked='addNewNode'
  />
  <v-menu
    v-model="showContextMenu"
    :position-x="contextMenuX"
    :position-y="contextMenuY"
    absolute
    offset-y
  >
    <v-list>
      <v-list-item-group>
        <v-list-item @click="onAddNodeMenuClicked">
          <v-list-item-icon class='ctc-menu-icon'>
            <v-icon>add</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Add Node</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="selectedNodeId" @click="onAddLinkMenuClicked">
          <v-list-item-icon class='ctc-menu-icon'>
            <v-icon>add_link</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Add Link</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-item-group>
    </v-list>
  </v-menu>
</div>
</template>
<script>
/* eslint-disable no-underscore-dangle */
import ThreatCanvasControls from '@/components/threat-canvas/ThreatCanvasControls.vue';
import CollapsableLinkedMultiTidyTree from '@/components/charts/CollapsableLinkedMultiTidyTree.vue';
import GenericProperties from '@/components/threat-canvas/GenercProperties.vue';
import propertyTypes, { extendedPropertyTypes } from '@/constants/threatCanvas';
import AddNodePopup from '@/components/popup/AddNodePopup.vue';

export default {
  name: 'ThreatCanvasView',
  components: {
    AddNodePopup, GenericProperties, CollapsableLinkedMultiTidyTree, ThreatCanvasControls,
  },
  props: {
    canvasName: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGridOn: true,
      gridCellSize: 20,
      selectionProperties: { type: 'none'},
      showAddNodeDialog: false,
      showContextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      trees: [
      ],
      customLinks: [
      ],
      selectedNodeId: null,
      selectedLinksIds: null,
      isCreatingLink: false,
      newNodePosition: { x: 0, y: 0 },
    };
  },
  computed: {
    canvasStyle() {
      if (!this.isGridOn) return {};
      const cellSize = this.gridCellSize;
      // Use CSS variable for theme-aware grid color
      return {
        backgroundImage: 'radial-gradient(circle, var(--grid-dot-color, #cdcdcd65) 1px, transparent 1px)',
        backgroundSize: `${cellSize}px ${cellSize}px`,
        backgroundRepeat: 'repeat',
      };
    },
    filteredTrees() {
      const filterChildren = nodes => nodes.map(node => {
        const newNode = { ...node };

        // First recursively filter all children
        if (node.children) {
          newNode.children = filterChildren(node.children);
        }
        // Then apply wrapper-specific filtering
        if (node.properties?.type === 'subdomains_wrapper') {
          const subdomainFilters = (node.properties.filters || []).filter(Boolean);
          const ipFilters = (node.properties.ipFilters || []).filter(Boolean);

          const filterSubdomains = subdomains => subdomains
            .map(subdomain => {
              // Check subdomain-level filters (OR among filters)
              const matchesSubdomain = subdomainFilters.length === 0 ||
                subdomainFilters.some(filter => subdomain.properties.name.toLowerCase().includes(filter.toLowerCase()));

              // Filter IP children under this subdomain (OR among ipFilters)
              const filteredIpChildren = (subdomain.children || []).filter(ipNode => ipFilters.length === 0 ||
                ipFilters.some(filter => ipNode.properties.name.toLowerCase().includes(filter.toLowerCase())));

              // Only include if both subdomain and IP filters pass (AND between sets)
              if (matchesSubdomain && (ipFilters.length === 0 || filteredIpChildren.length > 0)) {
                return {
                  ...subdomain,
                  children: filteredIpChildren,
                };
              }
              return null;
            })
            .filter(Boolean);

          newNode.children = filterSubdomains(newNode.children);
        } else if (node.properties?.type === 'email_wrapper') {
          const emailFilters = (node.properties.filters || []).filter(Boolean);
          const filterSubtree = subtree => subtree.filter(child => {
            // Handle email nodes
            if (child.properties?.type === 'email') {
              const shouldShow = emailFilters.length === 0 ||
                emailFilters.some(filter => child.properties.name.toLowerCase().includes(filter.toLowerCase()));
              return shouldShow;
            }

            // For non-matching types, keep the node but filter its children
            if (child.children) {
              const filteredChildren = filterSubtree(child.children);
              return filteredChildren.length > 0;
            }
            return true;
          });

          newNode.children = filterSubtree(newNode.children);
        }

        return newNode;
      });

      return filterChildren(this.trees);
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);

    // Check for loaded canvas data in route params
    if (this.$route.params.canvasData) {
      this.loadCanvasData(this.$route.params.canvasData);
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleCanvasControl(action, payload) {
      if (action === 'grid-on-off') {
        this.isGridOn = payload;
      } else if (action === 'add-node') {
        this.openAddNodeDialog();
      } else if (action === 'add-link') {
        this.onAddLinkMenuClicked();
      } else if (action === 'delete') {
        this.handleDelete();
      } else if (action === 'grid-zoom-out') {
        this.gridCellSize += 5;
      } else if (action === 'grid-zoom-in') {
        if (this.gridCellSize > 5) {
          this.gridCellSize -= 5;
        }
      } else if (action === 'reset-grid') {
        this.gridCellSize = 20;
      } else if (action === 'order') {
        // eslint-disable-next-line no-underscore-dangle
        this.$refs[`tree-${this._uid}`].autoAlignTrees();
      } else if (action === 'reset-view') {
        // eslint-disable-next-line no-underscore-dangle
        this.$refs[`tree-${this._uid}`].resetView();
      } else if (action === 'zoom-in') {
        // eslint-disable-next-line no-underscore-dangle
        this.$refs[`tree-${this._uid}`].zoomIn();
      } else if (action === 'zoom-out') {
        // eslint-disable-next-line no-underscore-dangle
        this.$refs[`tree-${this._uid}`].zoomOut();
      } else if (action === 'save') {
        this.saveCanvas();
      } else if (action === 'load') {
        this.loadCanvas();
      }
    },
    handlePropertyChange(payload) {
      if (payload.type === propertyTypes.canvas) {
        // eslint-disable-next-line no-underscore-dangle
        this.$refs[`tree-${this._uid}`].updateCanvasDescription(payload.description);
        this.selectionProperties = payload;
      } else if (payload.type === propertyTypes.link) {
        const linkIndex = this.customLinks.findIndex(link => link.sourceId === this.selectedLinksIds.sourceId && link.targetId === this.selectedLinksIds.targetId);
        if (linkIndex !== -1) {
          const updatedLink = {
            sourceId: this.selectedLinksIds.sourceId,
            targetId: this.selectedLinksIds.targetId,
            properties: {
              type: 'link',
              label: payload.label,
              bidirectional: payload.bidirectional,
            },
          };
          this.$set(this.customLinks, linkIndex, updatedLink);
        }
      } else if (payload.type === 'custom') {
        const nodeIndex = this.trees.findIndex(node => node.id === this.selectedNodeId);
        if (nodeIndex !== -1) {
          const updatedNode = {
            ...this.trees[nodeIndex],
            properties: {
              type: 'custom',
              icon: payload.icon,
              name: payload.name,
            },
          };
          this.$set(this.trees, nodeIndex, updatedNode);
        }
      } else if (payload.type === extendedPropertyTypes.subdomains_wrapper || payload.type === extendedPropertyTypes.email_wrapper) {
        const findAndUpdateNode = nodes => {
          for (let i = 0; i < nodes.length; i += 1) {
            if (nodes[i].id === this.selectedNodeId) {
              const updatedNode = {
                ...nodes[i],
                properties: {
                  type: payload.type,
                  filters: payload.filters || [],
                  ...(payload.type === extendedPropertyTypes.subdomains_wrapper && { ipFilters: payload.ipFilters || [] }),
                },
              };
              this.$set(nodes, i, updatedNode);
              return true;
            }
            if (nodes[i].children && nodes[i].children.length > 0) {
              if (findAndUpdateNode(nodes[i].children)) {
                return true;
              }
            }
          }
          return false;
        };

        findAndUpdateNode(this.trees);
      }
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.isCreatingLink) {
        this.cancelLinkCreation();
      }
    },
    cancelLinkCreation() {
      this.isCreatingLink = false;
      this.selectedNodeId = null;
      // eslint-disable-next-line no-underscore-dangle
      this.$refs[`tree-${this._uid}`].stopLinkCreation();
    },
    onNodeSelected(data) {
      this.updateSelection(data.properties);
      if (this.isCreatingLink) {
        if (data.id !== this.selectedNodeId) {
          this.finalizeLink(data.id);
        }
      } else {
        this.selectedNodeId = data.id;
      }
    },
    onLinkSelected(data) {
      this.updateSelection(data.properties);
      this.selectedLinksIds = {sourceId: data.sourceId, targetId: data.targetId};
    },
    onCanvasSelected(data) {
      this.updateSelection(data);
      if (this.isCreatingLink) {
        this.cancelLinkCreation();
      } else {
        this.selectedNodeId = null;
      }
    },
    updateSelection(properties) {
      this.selectionProperties = {...properties};
    },
    openAddNodeDialog() {
      // Position new node at canvas center when added via button
      const canvas = this.$el?.querySelector('.ctc-view__content__canvas-wrapper__canvas');
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        this.newNodePosition = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }
      this.showAddNodeDialog = true;
    },
    addNewNode(payload) {
      this.showAddNodeDialog = false;
      this.trees.push(payload);
      this.$nextTick(() => {
        this.newNodePosition = { x: 0, y: 0 };
      });
    },
    openContextMenu(e) {
      this.contextMenuX = e.clientX;
      this.contextMenuY = e.clientY;
      this.showContextMenu = true;
      this.newNodePosition = { x: e.clientX, y: e.clientY };
    },
    onAddNodeMenuClicked() {
      this.showContextMenu = false;
      this.openAddNodeDialog();
    },
    onAddLinkMenuClicked() {
      this.showContextMenu = false;
      this.isCreatingLink = true;
      // eslint-disable-next-line no-underscore-dangle
      this.$refs[`tree-${this._uid}`].startLinkCreation(this.selectedNodeId);
    },
    finalizeLink(targetId) {
      if (this.selectedNodeId && targetId && this.selectedNodeId !== targetId) {
        this.customLinks.push({
          sourceId: this.selectedNodeId,
          targetId,
          properties: {
            type: 'link',
            bidirectional: true,
            label: '',
          },
        });
      }
      this.isCreatingLink = false;
      this.selectedNodeId = null;
      // eslint-disable-next-line no-underscore-dangle
      this.$refs[`tree-${this._uid}`].stopLinkCreation();
    },
    handleDelete() {
      if (this.selectedNodeId) {
        // Function to find and remove a node from the tree structure
        const findAndRemoveNode = (nodes, nodeId) => {
          for (let i = 0; i < nodes.length; i += 1) {
            if (nodes[i].id === nodeId) {
              // Found the node, remove it and its children
              const node = nodes[i];
              // Remove links connected to this node
              this.customLinks = this.customLinks.filter(link => {
                const isSource = link.sourceId === nodeId;
                const isTarget = link.targetId === nodeId;
                return !isSource && !isTarget;
              });

              // Remove the node
              nodes.splice(i, 1);

              // If node has children, remove their links too
              if (node.children) {
                node.children.forEach(child => {
                  this.customLinks = this.customLinks.filter(link => {
                    const isSource = link.sourceId === child.id;
                    const isTarget = link.targetId === child.id;
                    return !isSource && !isTarget;
                  });
                });
              }
              return true;
            }

            // If node has children, search recursively
            if (nodes[i].children && nodes[i].children.length > 0) {
              if (findAndRemoveNode(nodes[i].children, nodeId)) {
                return true;
              }
            }
          }
          return false;
        };

        // Start the search from the root nodes
        findAndRemoveNode(this.trees, this.selectedNodeId);

        this.selectedNodeId = null;
        this.selectionProperties = { type: 'none' };
      } else if (this.selectedLinksIds) {
        // Remove the selected link
        this.customLinks = this.customLinks.filter(link => !(link.sourceId === this.selectedLinksIds.sourceId &&
            link.targetId === this.selectedLinksIds.targetId));
        this.selectedLinksIds = null;
        this.selectionProperties = { type: 'none' };
      }
    },
    saveCanvas() {
      // Create a clean data structure without circular references
      const cleanTrees = this.trees.map(tree => {
        const cleanNode = node => {
          if (!node) return null;
          const clean = {
            id: node.id,
            properties: node.properties,
            children: node.children ? node.children.map(cleanNode) : null,
            _children: node._children ? node._children.map(cleanNode) : null,
          };
          return clean;
        };
        return cleanNode(tree);
      });

      // Clean nodeMap to remove circular references
      const cleanNodeMap = {};
      Object.entries(this.$refs[`tree-${this._uid}`].nodeMap).forEach(([key, value]) => {
        cleanNodeMap[key] = {
          x: value.x,
          y: value.y,
          treeIndex: value.treeIndex,
          data: {
            id: value.data.id,
            properties: value.data.properties,
          },
        };
      });

      // Clean treeOffsets to remove any potential circular references
      const cleanTreeOffsets = this.$refs[`tree-${this._uid}`].treeOffsets.map(offset => ({
        x: offset.x,
        y: offset.y,
      }));

      const canvasData = {
        trees: cleanTrees,
        customLinks: this.customLinks,
        nodePositions: cleanNodeMap,
        treeOffsets: cleanTreeOffsets,
        canvasName: this.canvasName,
        canvasDescription: this.$refs[`tree-${this._uid}`].properties.description,
      };
      try {
        const formattedName = this.canvasName.title.toLowerCase().replace(/\s+/g, '-');
        const fileName = `${formattedName}-${new Date().toISOString().split('T')[0]}.ctc`;
        const blob = new Blob([JSON.stringify(canvasData)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error saving canvas:', error);
      }
    },
    loadCanvas() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.ctc';

      input.onchange = e => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = event => {
            try {
              const canvasData = JSON.parse(event.target.result);
              this.$nextTick(() => {
                this.loadCanvasData(canvasData);
              });
            } catch (error) {
              console.error('Error loading canvas:', error);
            }
          };
          reader.readAsText(file);
        }
      };

      input.click();
    },
    loadCanvasData(canvasData) {
      // Update component state with loaded data
      this.trees = canvasData.trees;
      this.customLinks = canvasData.customLinks;
      // Update canvas name and description
      if (canvasData.canvasName) {
        this.$emit('update-canvas-name', {name: this.canvasName.name, title: canvasData.canvasName.title});
      }
      // Pass the data to the tree component
      this.$nextTick(() => {
        if (this.$refs[`tree-${this._uid}`]) {
          this.$refs[`tree-${this._uid}`].updateCanvasDescription(canvasData.canvasDescription || '');
          // Then load the rest of the data
          this.$refs[`tree-${this._uid}`].loadCanvasData(canvasData);
        }
      });
    },
  },
};
</script>
<style scoped lang='scss'>
@import '~@/assets/styles/design-system';
.ctc-view{
  display: flex;
  flex-direction: column;
  &__content{
    display: flex;
    flex-direction: row;
    &__properties{
      flex-basis: rem(300);
      border-right: 1px solid var(--border-primary);
      background: var(--bg-primary);
    }
    &__canvas-wrapper{
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      position: relative;
      &__controls{
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        background: transparent;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        pointer-events: none;
      }
      &__canvas{
        flex-grow: 10;
        min-height: calc(100vh - 235px);
        background: var(--bg-primary);
      }
    }
  }
}
.ctc-menu-icon {
  margin-right: rem(12);
  margin-top: rem(7);
  margin-bottom: rem(7);
}
</style>

<style lang="scss">
// Fix expansion panel backgrounds in the properties sidebar for dark mode
.ctc-view__content__properties {
  .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: transparent;
  }
}
</style>
